import { useCallback, useEffect, useMemo, useState } from "react";
import { api, ApiError } from "../api";
import { AddForm, IconButton, SectionHead, UndoNote, type Ended } from "./Section";
import { ErrorState, PageSkeleton } from "../shell/states";
import { useQueryParam, writeQueries } from "../urlState";

// Configuration → Access: who reaches what (the Access design, "The access screens").
//
// BY SCOPE, the main view. On the left, the tree of what you configure or may share: a district,
// its schools, that school year's classes, then cohorts. It starts where your access starts: a
// school's configurator's tree begins at their school, a teacher's holds their cohorts. The chosen
// scope shows who reaches it, in up to four sections: who configures it, who sees or changes its
// student work, who reads its School Improvement data (kept apart from student work), and, at
// Preva, a district or a content provider, who may use School Improvement's chat. A district's
// chat is turned on by an operator; then its configurators give it, to people or to everyone.
//
// - Access given here has a View/Edit choice and an "x" to end it.
// - Access flowing from above says where it comes from ("Edit through school Riverbend High")
//   and has no controls: it is changed where it was given. A person with both is one row.
// - Each row says how it began: "Given by Ana Ruiz", or "Created this".
// - The "+" on a section gives access: a person known or new (by email, from anywhere), View or
//   Edit, and several scopes at once, with a line saying what it reaches before it is given.
//
// BY PERSON (docs/DESIGN.md, "Roles"). The roles one person has been given within what you
// configure, broadest place first, many of one role in one place as one row; and a "+" to give
// them another (which role, where) without leaving to start again from a place. Access they have
// elsewhere is not shown. Their account (sign-in, disabling) is their own organization's.
//
// Who may give or end what is the database's (access.grant and access.end); an action you may not
// take is shown disabled with the reason.

export type ScopeType = "platform" | "district" | "school" | "class" | "cohort"
  | "content_provider" | "team";

export interface ScopeNode {
  type: ScopeType;
  id: string;
  name: string;
  parent: { type: ScopeType; id: string } | null;
  students: number | null;
  grade_levels: string[] | null;
  can: { configure: boolean; share: boolean };
}

type Relation = "configurator" | "operator" | "viewer" | "editor" | "data_reader" | "chatter"
  | "scoring_viewer" | "scoring_maintainer" | "scoring_lead"
  | "curriculum_reviewer" | "curriculum_writer";
type SectionKey = "configure" | "work" | "data" | "chat" | "scoring" | "curriculum";

export interface Item {
  relationship_id: string;
  relation: Relation;
  person: { subject_id: string; name: string | null; email: string | null;
            user_group_type: string | null; disabled: boolean };
  here: boolean;
  via: { type: ScopeType; id: string; name: string } | null;
  began: string;
  can_end: boolean;
}

interface ScopeAccess {
  scope: { type: ScopeType; id: string; name: string };
  can: { configure: boolean; share: boolean; chat?: boolean };
  sections: Partial<Record<SectionKey, Item[]>>;
  // Only a district has `enabled`; elsewhere chat is on wherever it is given (null).
  chat?: { enabled: boolean | null; everyone: boolean; can_enable: boolean };
}

export const KIND_LABEL: Record<ScopeType, string> = {
  platform: "Platform", district: "District", school: "School", class: "Class",
  cohort: "Cohort", content_provider: "Content provider", team: "Content team",
};

export const RELATION_LABEL: Record<Relation, string> = {
  configurator: "Configures", operator: "Operator", viewer: "View", editor: "Edit",
  data_reader: "Reads data",
  chatter: "Chat",
  scoring_viewer: "Views scoring", scoring_maintainer: "Maintains scoring",
  scoring_lead: "Leads scoring",
  curriculum_reviewer: "Reviews curricula", curriculum_writer: "Writes curricula",
};

const SECTION_TITLE = {
  configure: "Configure",
  work: "Student work",
  data: "School Improvement data",
  chat: "Chat",
  scoring: "Scoring",
  curriculum: "Curricula",
} as const;

const SECTION_LEDE = {
  configure: "Who changes settings and access here.",
  work: "Who sees this work, or also changes it: scores, feedback, releasing, a Drive.",
  data: "Who reads its School Improvement data. Kept apart from student work.",
  chat: "Who may use School Improvement's chat. Each answer is an AI call Preva pays for.",
  scoring: "Who uses Scoring. A viewer reads raters and scorecards; a maintainer also builds "
    + "evaluation sets and runs experiments, which call the model; a lead also records scorecards.",
  curriculum: "Who changes and publishes this district's own version of a curriculum.",
} as const;

// On the platform the curricula section is about reviewing, not writing.
const PLATFORM_CURRICULUM_LEDE = "Who at Preva views every provider's curricula, drafts included. "
  + "Only for operators: the role does nothing for anyone else.";

const key = (s: { type: string; id: string }) => `${s.type}:${s.id}`;

/** The tree's children, by parent key; the roots are those whose parent is not in the tree. Pure. */
export function childrenOf(nodes: ScopeNode[]): { roots: ScopeNode[]; kids: Map<string, ScopeNode[]> } {
  const present = new Set(nodes.map(key));
  const kids = new Map<string, ScopeNode[]>();
  const roots: ScopeNode[] = [];
  for (const n of nodes) {
    if (n.parent && present.has(key(n.parent))) {
      const k = key(n.parent);
      kids.set(k, [...(kids.get(k) ?? []), n]);
    } else {
      roots.push(n);
    }
  }
  return { roots, kids };
}

/** What giving access on these scopes reaches, in words: "2 cohorts, 54 students". Pure. */
export function reachLine(nodes: ScopeNode[], chosen: string[]): string {
  const { kids } = childrenOf(nodes);
  const cohorts = new Map<string, ScopeNode>();
  const walk = (n: ScopeNode) => {
    if (n.type === "cohort") cohorts.set(key(n), n);
    for (const c of kids.get(key(n)) ?? []) walk(c);
  };
  const byKey = new Map(nodes.map((n) => [key(n), n]));
  for (const k of chosen) {
    const n = byKey.get(k);
    if (n) walk(n);
  }
  const students = [...cohorts.values()].reduce((sum, c) => sum + (c.students ?? 0), 0);
  const ch = cohorts.size === 1 ? "1 cohort" : `${cohorts.size} cohorts`;
  const stu = students === 1 ? "1 student" : `${students} students`;
  return `${ch}, ${stu}`;
}

export interface PersonRow {
  person: Item["person"];
  here: Item | null;      // given on this scope: the one with controls
  above: Item[];          // reaching it from a district, school or class above
}

/** One row per person: their access given here, if any, and what reaches them from above. A
 *  person with both is one person, not two lines that read like a duplicate. In the order the
 *  server sent them. Pure. */
export function byPerson(items: Item[]): PersonRow[] {
  const rows = new Map<string, PersonRow>();
  for (const it of items) {
    const k = it.person.subject_id;
    const row = rows.get(k) ?? { person: it.person, here: null, above: [] };
    if (it.here && !row.here) row.here = it;
    else row.above.push(it);
    rows.set(k, row);
  }
  return [...rows.values()];
}

/** Where access reaching from above comes from, in words: "Edit through class English 11". */
export function throughLine(it: Item, also: boolean): string {
  const from = `${KIND_LABEL[it.via!.type].toLowerCase()} ${it.via!.name}`;
  return `${also ? "Also " : ""}${RELATION_LABEL[it.relation]} through ${from}`;
}

/** The scopes a "+" may give on at once: this one, and its siblings the caller may share. Pure. */
export function siblings(nodes: ScopeNode[], scope: ScopeNode): ScopeNode[] {
  const same = (a: ScopeNode["parent"], b: ScopeNode["parent"]) =>
    (a === null && b === null) || (!!a && !!b && a.type === b.type && a.id === b.id);
  return nodes.filter((n) => n.type === scope.type && same(n.parent, scope.parent)
    && (n.can.share || n.can.configure));
}

const said = (e: unknown) => e instanceof ApiError ? (e.detail ?? e.message) : "That didn't work.";

/** Where each role can be given (app/access/schema.py), so "where" offers only those places. */
export const ROLE_AT: Record<Relation, ScopeType[]> = {
  operator: ["platform"], configurator: ["district", "school", "content_provider", "team"],
  editor: ["district", "school", "class", "cohort"], viewer: ["district", "school", "class", "cohort"],
  data_reader: ["district", "school"], chatter: ["platform", "district", "content_provider"],
  scoring_viewer: ["platform"], scoring_maintainer: ["platform"], scoring_lead: ["platform"],
  curriculum_reviewer: ["platform"], curriculum_writer: ["district"],
};

/** The places a role may be given at by the caller: where it exists, and they configure (or, for
 *  View and Edit, may share). Pure. */
export function placesFor(relation: Relation, nodes: ScopeNode[]): ScopeNode[] {
  const work = relation === "viewer" || relation === "editor";
  return nodes.filter((n) => ROLE_AT[relation].includes(n.type)
    && (n.can.configure || (work && n.can.share)));
}

// Broadest first: the order a person's roles are read in.
const KIND_ORDER: ScopeType[] = ["platform", "content_provider", "team", "district", "school",
                                 "class", "cohort"];

export interface Given {
  relationship_id: string; relation: Relation; below: number; can_end: boolean; since: string | null;
  scope: { type: ScopeType; id: string; name: string };
  parent: { type: ScopeType; id: string; name: string } | null;
}

export type RoleRow = { many: false; item: Given }
  | { many: true; relation: Relation; type: ScopeType; parent: Given["parent"]; items: Given[] };

/** A person's roles as rows, broadest place first. Four or more of one role at one kind of place
 *  in the same place above collapse into one row ("Edit · 17 cohorts in Riverbend High"). Pure. */
export function roleRows(access: Given[], collapseAt = 4): RoleRow[] {
  const sorted = [...access].sort((a, b) =>
    KIND_ORDER.indexOf(a.scope.type) - KIND_ORDER.indexOf(b.scope.type)
    || RELATION_LABEL[a.relation].localeCompare(RELATION_LABEL[b.relation])
    || (a.parent?.name ?? "").localeCompare(b.parent?.name ?? "")
    || a.scope.name.localeCompare(b.scope.name));
  const groups = new Map<string, Given[]>();
  for (const g of sorted) {
    const k = `${g.relation}|${g.scope.type}|${g.parent ? key(g.parent) : ""}`;
    groups.set(k, [...(groups.get(k) ?? []), g]);
  }
  const out: RoleRow[] = [];
  for (const items of groups.values()) {
    if (items.length >= collapseAt) {
      out.push({ many: true, relation: items[0].relation, type: items[0].scope.type,
                 parent: items[0].parent, items });
    } else {
      items.forEach((item) => out.push({ many: false, item }));
    }
  }
  return out;
}

/** "17 cohorts in Riverbend High". Pure. */
export function manyLine(r: Extract<RoleRow, { many: true }>): string {
  const kind = KIND_LABEL[r.type].toLowerCase();
  const n = r.items.length;
  return `${n} ${kind === "class" ? "classes" : `${kind}s`}${r.parent ? ` in ${r.parent.name}` : ""}`;
}

// What is open is in the URL (docs/DESIGN.md, "Links and the URL"): `?place=` on By place,
// `?view=person` while choosing someone, `?person=` once chosen, and `?year=`.
export function Access() {
  const [person] = useQueryParam("person");
  const [view] = useQueryParam("view");
  const [place, setPlace] = useQueryParam("place");
  const [yearQ] = useQueryParam("year");
  const [defaultYear, setDefaultYear] = useState<number | null>(null);
  const year = yearQ ? Number(yearQ) : defaultYear;
  const [years, setYears] = useState<number[]>([]);
  const [nodes, setNodes] = useState<ScopeNode[] | null>(null);
  const [error, setError] = useState<{ status: number; message: string } | null>(null);
  const picking = view === "person" && !person;
  const onPerson = (id: string | null) => writeQueries({ person: id, view: null });

  const loadTree = useCallback(() => {
    api.get<{ school_year: number | null; years: number[]; scopes: ScopeNode[] }>(
      `/configuration/scopes${year ? `?school_year=${year}` : ""}`)
      .then((d) => {
        setNodes(d.scopes);
        setYears(d.years ?? []);
        if (year === null && d.school_year) setDefaultYear(d.school_year);
      })
      .catch((e: unknown) => setError(e instanceof ApiError
        ? { status: e.status, message: e.detail ?? e.message }
        : { status: 0, message: "could not be read" }));
  }, [year]);

  useEffect(() => { loadTree(); }, [loadTree]);

  if (error?.status === 403) {
    return (
      <div className="gl">
        <h2>Access</h2>
        <p className="gl-lede">You do not configure anything, or give access to anything, yet.</p>
      </div>
    );
  }
  if (error) return <ErrorState />;
  if (!nodes) return <PageSkeleton />;

  const ways = (
    <div className="acc-ways" role="tablist" aria-label="Access">
      <button type="button" role="tab" aria-selected={!person && !picking}
              className={"btn sm" + (!person && !picking ? " primary" : "")}
              onClick={() => writeQueries({ person: null, view: null })}>By place</button>
      <button type="button" role="tab" aria-selected={!!person || picking}
              className={"btn sm" + (person || picking ? " primary" : "")}
              onClick={() => { if (!person) writeQueries({ view: "person" }); }}>By person</button>
    </div>
  );

  if (person) {
    return <ByPerson subjectId={person} nodes={nodes} ways={ways} onChanged={loadTree} />;
  }
  if (picking) {
    return (
      <div className="gl acc">
        <h2>Access</h2>
        {ways}
        <PersonPicker onPick={(id) => onPerson(id)} />
      </div>
    );
  }

  const chosen = place ?? (nodes[0] ? key(nodes[0]) : null);
  const scope = nodes.find((n) => key(n) === chosen) ?? null;
  return (
    <div className="gl acc">
      <div className="acc-head">
        <h2>Access</h2>
        {years.length > 1 && (
          <label className="acc-year">
            School year
            <select value={year ?? ""} onChange={(e) => writeQueries(
                      { year: e.target.value, place: null }, { replace: true })}>
              {years.map((y) => <option key={y} value={y}>{`${y - 1}–${String(y).slice(2)}`}</option>)}
            </select>
          </label>
        )}
      </div>
      {ways}
      <p className="gl-lede">Choose a place.</p>
      <div className="acc-body">
        <ScopeTree nodes={nodes} chosen={chosen} year={year} onChoose={(k) => setPlace(k)}
                   onChanged={loadTree} />
        {scope ? (
          <ScopePanel key={chosen ?? ""} scope={scope} nodes={nodes} onPerson={onPerson}
                      onChanged={loadTree} />
        ) : (
          <p className="rv-mut">Choose something on the left.</p>
        )}
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------- the tree

function ScopeTree({ nodes, chosen, year, onChoose, onChanged }: {
  nodes: ScopeNode[]; chosen: string | null; year: number | null;
  onChoose: (k: string) => void; onChanged: () => void;
}) {
  const { roots, kids } = useMemo(() => childrenOf(nodes), [nodes]);
  const [adding, setAdding] = useState<string | null>(null);

  const render = (n: ScopeNode, depth: number) => {
    const k = key(n);
    const canAdd = (n.type === "school" || n.type === "class") && (n.can.configure || n.can.share);
    return (
      <li key={k}>
        <div className={`acc-node${chosen === k ? " on" : ""}`} style={{ paddingLeft: `${depth}rem` }}>
          <button type="button" className="acc-pick" onClick={() => onChoose(k)}
                  aria-current={chosen === k ? "true" : undefined}>
            <span className="acc-kind">{KIND_LABEL[n.type]}</span> {n.name}
            {n.type === "cohort" && n.students !== null &&
              <span className="d"> · {n.students}</span>}
          </button>
          {canAdd && (
            <IconButton icon="plus" size="item"
                        label={n.type === "school" ? `Add a class to ${n.name}`
                                                   : `Add a cohort to ${n.name}`}
                        pressed={adding === k}
                        onClick={() => setAdding(adding === k ? null : k)} />
          )}
        </div>
        {adding === k && (
          <AddScope parent={n} year={year} onDone={() => { setAdding(null); onChanged(); }}
                    onCancel={() => setAdding(null)} />
        )}
        {(kids.get(k) ?? []).length > 0 && (
          <ul>{(kids.get(k) ?? []).map((c) => render(c, depth + 1))}</ul>
        )}
      </li>
    );
  };
  return <nav className="acc-tree" aria-label="What you configure"><ul>{roots.map((r) => render(r, 0))}</ul></nav>;
}

interface Course { course_id: string; name: string; grade_levels: string[] | null }

function AddScope({ parent, year, onDone, onCancel }: {
  parent: ScopeNode; year: number | null; onDone: () => void; onCancel: () => void;
}) {
  const [courses, setCourses] = useState<Course[] | null>(null);
  const [course, setCourse] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Under a school: a class (a course, this year). Under a class: a cohort (a section of it).
  const aClass = parent.type === "school";

  useEffect(() => {
    if (!aClass) return;
    api.get<{ courses: Course[] }>(`/configuration/courses?school=${encodeURIComponent(parent.id)}`)
      .then((d) => setCourses(d.courses)).catch((e) => setError(said(e)));
  }, [aClass, parent.id]);

  const save = () => {
    setBusy(true);
    setError(null);
    const call = aClass
      ? api.post("/configuration/classes", { course_id: course, school_id: parent.id,
                                             school_year: year })
      : api.post("/configuration/cohorts", { name: name.trim(), class_id: parent.id });
    call.then(onDone).catch((e) => setError(said(e))).finally(() => setBusy(false));
  };

  return (
    <AddForm saveLabel={aClass ? "Add class" : "Add cohort"} busy={busy} error={error}
             missing={aClass ? (!course ? "Choose the course." : year === null ? "Choose a school year at the top first." : null)
                              : (!name.trim() ? "Enter the cohort name." : null)}
             onSave={save} onCancel={onCancel}
             note={aClass ? "One course, at this school, this school year. You will edit it."
                          : "A cohort of this class. You will edit it. Its students come in from its Drive folders."}>
      {aClass ? (
        <label>
          Course
          <select value={course} autoFocus onChange={(e) => setCourse(e.target.value)}>
            <option value="">Choose…</option>
            {(courses ?? []).map((c) => (
              <option key={c.course_id} value={c.course_id}>
                {c.name}{c.grade_levels?.length ? ` (grade ${c.grade_levels.join(", ")})` : ""}
              </option>
            ))}
          </select>
        </label>
      ) : (
        <label>
          Cohort name
          <input value={name} autoFocus onChange={(e) => setName(e.target.value)}
                 placeholder="Section 1" />
        </label>
      )}
    </AddForm>
  );
}

// --------------------------------------------------------------------------- one scope

function ScopePanel({ scope, nodes, onPerson, onChanged }: {
  scope: ScopeNode; nodes: ScopeNode[]; onPerson?: (s: string) => void; onChanged: () => void;
}) {
  const [data, setData] = useState<ScopeAccess | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [giving, setGiving] = useState<keyof typeof SECTION_TITLE | null>(null);
  const [busy, setBusy] = useState(false);
  const [ended, setEnded] = useState<Ended | null>(null);

  const load = useCallback(() => {
    api.get<ScopeAccess>(`/configuration/access?type=${scope.type}&id=${encodeURIComponent(scope.id)}`)
      .then(setData).catch((e) => setError(said(e)));
  }, [scope.type, scope.id]);
  useEffect(() => { load(); }, [load]);

  async function act(run: () => Promise<unknown>, then?: () => void) {
    setBusy(true);
    setError(null);
    setEnded(null);
    try {
      await run();
      then?.();
      load();
      onChanged();
    } catch (e) {
      setError(said(e));
    } finally {
      setBusy(false);
    }
  }

  if (!data) return error ? <p className="inc-error">{error}</p>
                          : <div className="muted dots">loading who reaches {scope.name}</div>;

  const sections = Object.entries(data.sections) as [keyof typeof SECTION_TITLE, Item[]][];
  return (
    <section className="acc-panel" aria-label={`Access to ${scope.name}`}>
      <h3 className="acc-title"><span className="acc-kind">{KIND_LABEL[scope.type]}</span> {scope.name}</h3>
      <UndoNote ended={ended} busy={busy} onUndo={() => ended && act(ended.undo)} />
      {sections.map(([section, items]) => {
        const mayGive = section === "work" ? data.can.share
          : section === "chat" ? !!data.can.chat : data.can.configure;
        const chatWhy = scope.type === "district"
          ? "Only this district's configurators, or Preva, give chat."
          : "Only Preva gives chat here.";
        return (
          <div key={section} className="pp-panel">
            <SectionHead title={SECTION_TITLE[section]}
                         addLabel={`Give someone ${SECTION_TITLE[section].toLowerCase()} access to ${scope.name}`}
                         adding={giving === section}
                         onAdd={() => setGiving(giving === section ? null : section)}
                         disabledWhy={mayGive ? null : section === "work"
                           ? "Giving access to this work is for its configurators and editors."
                           : section === "chat" ? chatWhy
                           : section === "scoring" ? "Only Preva's operators give scoring roles."
                           : section === "curriculum" && scope.type === "platform"
                             ? "Only Preva's operators give this."
                           : "Only its configurators give this."} />
            <p className="rv-mut">{section === "curriculum" && scope.type === "platform"
              ? PLATFORM_CURRICULUM_LEDE : SECTION_LEDE[section]}</p>
            {section === "chat" && data.chat && (
              <ChatSwitches name={scope.name} chat={data.chat} mayGive={mayGive} why={chatWhy}
                            busy={busy}
                            onSwitch={(body) => act(() => api.put("/configuration/chat", {
                              type: scope.type, id: scope.id, ...body }))} />
            )}
            {giving === section && (
              <Give scope={scope} nodes={nodes} section={section} busy={busy}
                    onCancel={() => setGiving(null)}
                    onGive={(body) => act(() => api.post("/configuration/access", body))
                      .then(() => setGiving(null))} />
            )}
            {items.length === 0 ? (
              <p className="rv-mut">None yet.</p>
            ) : (
              <ul className="acc-list">
                {byPerson(items).map(({ person, here, above }) => {
                  // The row's controls are for what is given here; without it, the strongest
                  // access from above is shown, and changed where it was given.
                  const it = here ?? above[0];
                  return (
                  <li key={person.subject_id} className={person.disabled ? "pp-disabled" : undefined}>
                    <div>
                      {onPerson ? (
                        <button type="button" className="btn-link"
                                onClick={() => onPerson(person.subject_id)}>
                          {person.name || person.email}
                        </button>
                      ) : (person.name || person.email)}
                      {here && <div className="d">{here.began}</div>}
                      {above.map((a) => (
                        <div key={a.relationship_id} className="d">{throughLine(a, !!here)}</div>
                      ))}
                    </div>
                    <div className="cell-edit">
                      {it.here && section === "work" && it.can_end ? (
                        <select aria-label={`${it.person.name || it.person.email}'s access`}
                                value={it.relation} disabled={busy}
                                onChange={(e) => act(async () => {
                                  await api.post("/configuration/access", {
                                    subject_id: it.person.subject_id, relation: e.target.value,
                                    scopes: [{ type: scope.type, id: scope.id }] });
                                  await api.del(`/configuration/access/${it.relationship_id}`);
                                })}>
                          <option value="viewer">View</option>
                          <option value="editor">Edit</option>
                        </select>
                      ) : (
                        <span>{RELATION_LABEL[it.relation]}</span>
                      )}
                      {it.here && (
                        <IconButton icon="x" size="item"
                                    label={`End ${it.person.name || it.person.email}'s access here`}
                                    disabled={busy || !it.can_end}
                                    why={section === "chat" ? chatWhy
                                                            : "Only its configurators end this."}
                                    onClick={() => act(() => api.del(
                                      `/configuration/access/${it.relationship_id}`), () => setEnded({
                                      said: `Ended ${it.person.name || it.person.email}'s ${
                                        RELATION_LABEL[it.relation]} at ${scope.name}.`,
                                      undo: () => api.post("/configuration/access", {
                                        subject_id: it.person.subject_id, relation: it.relation,
                                        scopes: [{ type: scope.type, id: scope.id }] }) }))} />
                      )}
                    </div>
                  </li>
                  );
                })}
              </ul>
            )}
          </div>
        );
      })}
      {error && <p className="inc-error">{error}</p>}
    </section>
  );
}

interface Known { subject_id: string; name: string | null; email: string | null }

/** A scope's chat switches: on for a district (an operator's), and for everyone in it. */
function ChatSwitches({ name, chat, mayGive, why, busy, onSwitch }: {
  name: string; chat: NonNullable<ScopeAccess["chat"]>; mayGive: boolean; why: string;
  busy: boolean; onSwitch: (body: { enabled?: boolean; everyone?: boolean }) => void;
}) {
  return (
    <ul className="acc-list">
      {chat.enabled !== null && (
        <li>
          <div>
            {chat.enabled ? `Chat is on for ${name}` : `Chat is off for ${name}`}
            <div className="d">
              {chat.enabled ? "Turned on by Preva."
                : "Until Preva turns it on, nobody here can chat, even people given it below."}
            </div>
          </div>
          <div className="cell-edit">
            <IconButton icon={chat.enabled ? "lock" : "unlock"} size="row"
                        label={chat.enabled ? `Turn chat off for ${name}` : `Turn chat on for ${name}`}
                        disabled={busy || !chat.can_enable}
                        why="Preva turns chat on for a district."
                        onClick={() => onSwitch({ enabled: !chat.enabled })} />
          </div>
        </li>
      )}
      <li>
        <div>
          {chat.everyone ? `Everyone in ${name}` : `Not everyone in ${name}`}
          <div className="d">
            {chat.everyone ? "Every member has chat, including anyone who joins later."
              : "Only the people listed below."}
          </div>
        </div>
        <div className="cell-edit">
          <IconButton icon={chat.everyone ? "x" : "plus"} size="item"
                      label={chat.everyone ? `Stop giving chat to everyone in ${name}`
                                           : `Give chat to everyone in ${name}`}
                      disabled={busy || !mayGive} why={why}
                      onClick={() => onSwitch({ everyone: !chat.everyone })} />
        </div>
      </li>
    </ul>
  );
}

function Give({ scope, nodes, section, busy, onGive, onCancel }: {
  scope: ScopeNode; nodes: ScopeNode[]; section: SectionKey; busy: boolean;
  onGive: (body: unknown) => void; onCancel: () => void;
}) {
  const [who, setWho] = useState("");
  const [relation, setRelation] = useState<Relation>(
    section === "configure" ? (scope.type === "platform" ? "operator" : "configurator")
      : section === "data" ? "data_reader"
      : section === "chat" ? "chatter" : section === "scoring" ? "scoring_viewer"
      : section === "curriculum"
        ? (scope.type === "platform" ? "curriculum_reviewer" : "curriculum_writer")
      : "viewer");
  const [scopes, setScopes] = useState<string[]>([key(scope)]);
  const [known, setKnown] = useState<Known[]>([]);
  const options = section === "work" ? siblings(nodes, scope) : [scope];

  useEffect(() => {
    // Configurators can see everyone; someone who only shares their cohort types an address.
    api.get<{ people: Known[] }>("/people").then((d) => setKnown(d.people ?? [])).catch(() => {});
  }, []);

  const match = known.find((k) => (k.email ?? "").toLowerCase() === who.trim().toLowerCase()
    || (k.name ?? "").toLowerCase() === who.trim().toLowerCase());
  const email = !match && who.includes("@") ? who.trim() : null;
  const byKey = new Map(nodes.map((n) => [key(n), n]));

  return (
    <AddForm saveLabel="Give access" busy={busy} onCancel={onCancel}
             missing={!match && !email ? "Enter a name or email." : scopes.length === 0 ? "Choose at least one place." : null}
             onSave={() => onGive({
               relation, ...(match ? { subject_id: match.subject_id } : { email }),
               scopes: scopes.map((k) => ({ type: byKey.get(k)!.type, id: byKey.get(k)!.id })) })}
             note={section === "work"
               ? `Reaches ${reachLine(nodes, scopes)}.`
                 + (email ? " They are invited, and can sign in with that address." : "")
               : email ? "They are invited, and can sign in with that address." : undefined}>
      <label>
        Name or email
        <input value={who} autoFocus list="acc-known" onChange={(e) => setWho(e.target.value)}
               placeholder="someone@district.org" />
        <datalist id="acc-known">
          {known.map((k) => <option key={k.subject_id} value={k.email ?? k.name ?? ""}>
            {k.name ?? ""}</option>)}
        </datalist>
      </label>
      {section === "work" && (
        <label>
          Access
          <select value={relation} onChange={(e) => setRelation(e.target.value as Relation)}>
            <option value="viewer">View</option>
            <option value="editor">Edit</option>
          </select>
        </label>
      )}
      {section === "scoring" && (
        <label>
          Role
          <select value={relation} onChange={(e) => setRelation(e.target.value as Relation)}>
            <option value="scoring_viewer">{RELATION_LABEL.scoring_viewer}</option>
            <option value="scoring_maintainer">{RELATION_LABEL.scoring_maintainer}</option>
            <option value="scoring_lead">{RELATION_LABEL.scoring_lead}</option>
          </select>
        </label>
      )}
      {options.length > 1 && (
        <fieldset className="acc-scopes">
          <legend>Where</legend>
          {options.map((n) => (
            <label key={key(n)} className="acc-check">
              <input type="checkbox" checked={scopes.includes(key(n))}
                     onChange={(e) => setScopes(e.target.checked
                       ? [...scopes, key(n)] : scopes.filter((s) => s !== key(n)))} />
              {n.name}
              {n.grade_levels?.length ? <span className="d"> · grade {n.grade_levels.join(", ")}</span> : null}
            </label>
          ))}
        </fieldset>
      )}
    </AddForm>
  );
}

// --------------------------------------------------------------------------- one person

interface PersonAccess {
  person: { subject_id: string; display_name: string | null; email: string | null;
            user_group_type: string | null; disabled: boolean };
  access: Given[];
}

interface Known { subject_id: string; name: string | null; email: string | null }

/** Choose someone, by name or email, to see and give their roles. */
function PersonPicker({ onPick }: { onPick: (subjectId: string) => void }) {
  const [known, setKnown] = useState<Known[] | null>(null);
  const [who, setWho] = useState("");
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    api.get<{ people: Known[] }>("/people").then((d) => setKnown(d.people ?? []))
      .catch((e) => setError(said(e)));
  }, []);
  const match = (known ?? []).find((k) => [k.email, k.name].some(
    (v) => (v ?? "").toLowerCase() === who.trim().toLowerCase()));
  return (
    <section className="pp-panel">
      <p className="gl-lede">Choose someone.</p>
      <div className="pp-form">
        <label>Name or email
          <input value={who} list="acc-people" autoFocus onChange={(e) => setWho(e.target.value)}
                 placeholder="someone@district.org" />
          <datalist id="acc-people">
            {(known ?? []).map((k) => <option key={k.subject_id} value={k.email ?? k.name ?? ""}>{k.name ?? ""}</option>)}
          </datalist>
        </label>
        <div className="pp-actions">
          <button type="button" className="btn primary sm" disabled={!match}
                  onClick={() => match && onPick(match.subject_id)}>Open</button>
        </div>
      </div>
      {error && <p className="inc-error">{error}</p>}
    </section>
  );
}

const GIVE_ORDER: Relation[] = ["configurator", "editor", "viewer", "data_reader", "chatter",
  "curriculum_writer", "operator", "scoring_viewer", "scoring_maintainer", "scoring_lead",
  "curriculum_reviewer"];

/** Give this person a role: which role, and where (only the places it exists at and you may give
 *  it at). */
function GiveToPerson({ who, subjectId, nodes, busy, onGive, onCancel, error }: {
  who: string; subjectId: string; nodes: ScopeNode[]; busy: boolean; error: string | null;
  onGive: (body: unknown) => void; onCancel: () => void;
}) {
  const roles = GIVE_ORDER.filter((r) => placesFor(r, nodes).length > 0);
  const [relation, setRelation] = useState<Relation | "">(roles[0] ?? "");
  const places = relation ? placesFor(relation, nodes) : [];
  const [where, setWhere] = useState("");
  const chosen = places.find((n) => key(n) === where) ?? (places.length === 1 ? places[0] : null);
  return (
    <AddForm saveLabel="Give it" busy={busy} error={error} onCancel={onCancel}
             missing={!relation ? "Choose a role." : !chosen ? "Choose where." : null}
             onSave={() => chosen && onGive({ relation, subject_id: subjectId,
                                             scopes: [{ type: chosen.type, id: chosen.id }] })}>
      <label>Role
        <select value={relation} onChange={(e) => { setRelation(e.target.value as Relation); setWhere(""); }}>
          {roles.map((r) => <option key={r} value={r}>{RELATION_LABEL[r]}</option>)}
        </select>
      </label>
      <label>Where
        <select value={chosen ? key(chosen) : where} onChange={(e) => setWhere(e.target.value)}>
          {places.length !== 1 && <option value="">Choose…</option>}
          {places.map((n) => <option key={key(n)} value={key(n)}>{KIND_LABEL[n.type]} · {n.name}</option>)}
        </select>
      </label>
      {roles.length === 0 && <p className="rv-mut">You configure nowhere you could give {who} a role.</p>}
    </AddForm>
  );
}

function ByPerson({ subjectId, nodes, ways, onChanged }: {
  subjectId: string; nodes: ScopeNode[]; ways: React.ReactNode; onChanged: () => void;
}) {
  const [data, setData] = useState<PersonAccess | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [giving, setGiving] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const [ended, setEnded] = useState<Ended | null>(null);
  const load = useCallback(() => {
    api.get<PersonAccess>(`/configuration/people/${encodeURIComponent(subjectId)}/access`)
      .then(setData).catch((e) => setError(said(e)));
  }, [subjectId]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="gl">{error ? <p className="inc-error">{error}</p>
                                               : <div className="muted dots">loading</div>}</div>;
  const who = data.person.display_name || data.person.email || "This person";
  const run = (call: () => Promise<unknown>, then?: () => void) => {
    setBusy(true); setError(null); setEnded(null);
    call().then(() => { then?.(); load(); })
      .catch((e) => setError(said(e))).finally(() => setBusy(false));
  };
  const end = (g: Given) => run(() => api.del(`/configuration/access/${g.relationship_id}`), () => setEnded({
    said: `Ended ${who}'s ${RELATION_LABEL[g.relation]} at ${g.scope.name}.`,
    undo: () => api.post("/configuration/access", {
      subject_id: subjectId, relation: g.relation, scopes: [{ type: g.scope.type, id: g.scope.id }] }) }));
  const row = (g: Given, indent = false) => (
    <tr key={g.relationship_id}>
      <td>{indent ? "" : RELATION_LABEL[g.relation]}</td>
      <td className={indent ? "ct-indent" : undefined}>
        <span className="acc-kind">{KIND_LABEL[g.scope.type]}</span> {g.scope.name}
        {g.below > 0 && <span className="d"> and {g.below} below</span>}
      </td>
      <td className="d">{g.since ? g.since.slice(0, 10) : ""}</td>
      <td className="r">
        <IconButton icon="x" size="item" label={`End ${who}'s ${RELATION_LABEL[g.relation]} at ${g.scope.name}`}
                    disabled={busy || !g.can_end} why="Only its configurators end this." onClick={() => end(g)} />
      </td>
    </tr>
  );
  const rows = roleRows(data.access);
  return (
    <div className="gl acc">
      <h2>{who}</h2>
      {ways}
      <UndoNote ended={ended} busy={busy} onUndo={() => ended && run(ended.undo)} />
      <section className="pp-panel">
        <SectionHead title="Roles" addLabel={`Give ${who} a role`} adding={giving}
                     onAdd={() => { setGiving(!giving); setError(null); }} />
        {giving && (
          <GiveToPerson who={who} subjectId={subjectId} nodes={nodes} busy={busy} error={error}
                        onCancel={() => setGiving(false)}
                        onGive={(body) => {
                          setBusy(true); setError(null);
                          api.post("/configuration/access", body)
                            .then(() => { setGiving(false); load(); onChanged(); })
                            .catch((e) => setError(said(e))).finally(() => setBusy(false));
                        }} />
        )}
        {rows.length === 0 ? <p className="rv-mut">None yet.</p> : (
          <table className="tbl">
            <thead><tr><th>Role</th><th>Where</th><th>Since</th><th /></tr></thead>
            <tbody>
              {rows.flatMap((r) => {
                if (!r.many) return [row(r.item)];
                const k = `${r.relation}|${r.type}|${r.parent ? key(r.parent) : ""}`;
                return [
                  <tr key={k}>
                    <td>{RELATION_LABEL[r.relation]}</td>
                    <td>{manyLine(r)}</td>
                    <td />
                    <td className="r">
                      <button type="button" className="btn-link" aria-expanded={open === k}
                              onClick={() => setOpen(open === k ? null : k)}>
                        {open === k ? "Hide" : "Show"}</button>
                    </td>
                  </tr>,
                  ...(open === k ? r.items.map((g) => row(g, true)) : []),
                ];
              })}
            </tbody>
          </table>
        )}
        {!giving && error && <p className="inc-error">{error}</p>}
      </section>
      <p className="rv-mut">Access {who} has elsewhere is not shown. Their account (sign-in,
        disabling) is looked after by their own organization.</p>
    </div>
  );
}
